import React, { useCallback, useEffect, useState } from "react";
import { Navigate, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
    LogOut,
    Plus,
    Trash2,
    Loader2,
    AlertTriangle,
    Users,
    Power,
    PencilLine,
} from "lucide-react";
import { useAuth } from "@/lib/auth";

/**
 * /admin/portal-users — admin page to create, edit, and disable customer
 * portal users. Each user has email (login id), display name, customer_id
 * (which maps to /Customers/<customer_id>/ in Dropbox), and an enabled flag.
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api/portal/admin`;

function formatDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString();
    } catch (_) {
        return "—";
    }
}

const EMPTY_FORM = {
    email: "",
    display_name: "",
    customer_id: "",
    notes: "",
};

export default function AdminPortalUsersPage() {
    const { token, logout } = useAuth();
    const navigate = useNavigate();
    const [users, setUsers] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [editingId, setEditingId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);

    const authHeaders = React.useMemo(
        () => ({ Authorization: `Bearer ${token}` }),
        [token]
    );

    const loadUsers = useCallback(async () => {
        setError(null);
        try {
            const { data } = await axios.get(`${API}/users`, {
                headers: authHeaders,
            });
            setUsers(data.users || []);
        } catch (e) {
            setError(
                e?.response?.data?.detail ||
                    "Could not load portal users. Try again."
            );
            setUsers([]);
        }
    }, [authHeaders]);

    useEffect(() => {
        document.title = "Admin · Portal Users — Endurance Casualty";
        loadUsers();
    }, [loadUsers]);

    const handleSignOut = () => {
        logout();
        navigate("/admin/login", { replace: true });
    };

    if (!token) return <Navigate to="/admin/login" replace />;

    const resetForm = () => {
        setForm(EMPTY_FORM);
        setEditingId(null);
        setError(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        setNotice(null);
        try {
            if (editingId) {
                await axios.put(`${API}/users/${editingId}`, form, {
                    headers: authHeaders,
                });
                setNotice("Portal user updated.");
            } else {
                await axios.post(`${API}/users`, form, { headers: authHeaders });
                setNotice("Portal user created.");
            }
            await loadUsers();
            resetForm();
        } catch (e) {
            setError(
                e?.response?.data?.detail ||
                    "Could not save. Check the fields and try again."
            );
        } finally {
            setSaving(false);
        }
    };

    const startEdit = (u) => {
        setForm({
            email: u.email,
            display_name: u.display_name,
            customer_id: u.customer_id,
            notes: u.notes || "",
        });
        setEditingId(u.id);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const toggleEnabled = async (u) => {
        try {
            await axios.put(
                `${API}/users/${u.id}`,
                { enabled: !u.enabled },
                { headers: authHeaders }
            );
            await loadUsers();
        } catch (e) {
            setError(e?.response?.data?.detail || "Could not toggle status.");
        }
    };

    const deleteUser = async (u) => {
        if (!window.confirm(`Delete portal user ${u.email}? This cannot be undone.`)) return;
        try {
            await axios.delete(`${API}/users/${u.id}`, { headers: authHeaders });
            await loadUsers();
        } catch (e) {
            setError(e?.response?.data?.detail || "Could not delete user.");
        }
    };

    return (
        <main className="min-h-screen bg-brand-bg text-brand-ivory">
            <header className="border-b border-[rgba(212,175,55,0.18)] bg-[#0b0e13]">
                <div className="mx-auto flex max-w-[1200px] items-center justify-between px-6 py-4 md:px-10">
                    <div>
                        <p className="text-[10px] uppercase tracking-[0.4em] text-brand-goldSoft">
                            Admin
                        </p>
                        <h1 className="font-serif text-xl text-brand-ivory">
                            Customer Portal Users
                        </h1>
                    </div>
                    <div className="flex items-center gap-3">
                        <Link
                            to="/admin/videos"
                            data-testid="admin-link-videos"
                            className="text-xs uppercase tracking-[0.2em] text-brand-gold2 hover:underline"
                        >
                            ← Videos
                        </Link>
                        <Link
                            to="/admin/blog"
                            data-testid="admin-link-blog"
                            className="text-xs uppercase tracking-[0.2em] text-brand-gold2 hover:underline"
                        >
                            Blog
                        </Link>
                        <Link
                            to="/admin/analytics"
                            data-testid="admin-link-analytics"
                            className="text-xs uppercase tracking-[0.2em] text-brand-gold2 hover:underline"
                        >
                            Analytics
                        </Link>
                        <button
                            type="button"
                            onClick={handleSignOut}
                            data-testid="admin-portal-users-signout"
                            className="inline-flex items-center gap-2 rounded-full border border-brand-gold/60 bg-[#0b0e13] px-4 py-2 text-xs uppercase tracking-[0.2em] text-brand-gold2 hover:bg-brand-gold hover:text-brand-bg"
                        >
                            <LogOut aria-hidden="true" className="h-3.5 w-3.5" />
                            Sign out
                        </button>
                    </div>
                </div>
            </header>

            <section className="mx-auto max-w-[1200px] px-6 py-10 md:px-10">
                {/* Form */}
                <div className="rounded-2xl border border-[rgba(212,175,55,0.25)] bg-[#0b0e13] p-6 md:p-8">
                    <div className="flex items-center gap-2">
                        {editingId ? (
                            <PencilLine
                                aria-hidden="true"
                                className="h-4 w-4 text-brand-gold2"
                            />
                        ) : (
                            <Plus
                                aria-hidden="true"
                                className="h-4 w-4 text-brand-gold2"
                            />
                        )}
                        <p className="text-[11px] uppercase tracking-[0.4em] text-brand-goldSoft">
                            {editingId ? "Edit portal user" : "Add a portal user"}
                        </p>
                    </div>
                    <form
                        onSubmit={handleSubmit}
                        className="mt-5 grid gap-4 md:grid-cols-2"
                    >
                        <label className="block">
                            <span className="text-[10px] uppercase tracking-[0.3em] text-brand-goldSoft">
                                Email (login id)
                            </span>
                            <input
                                type="email"
                                required
                                value={form.email}
                                onChange={(e) =>
                                    setForm({ ...form, email: e.target.value })
                                }
                                placeholder="customer@example.com"
                                className="mt-2 w-full rounded-lg border border-[rgba(212,175,55,0.3)] bg-[#0d1117] px-3 py-2 text-sm text-brand-ivory focus:border-brand-gold focus:outline-none"
                                disabled={!!editingId}
                            />
                        </label>
                        <label className="block">
                            <span className="text-[10px] uppercase tracking-[0.3em] text-brand-goldSoft">
                                Display name
                            </span>
                            <input
                                type="text"
                                required
                                value={form.display_name}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        display_name: e.target.value,
                                    })
                                }
                                placeholder="Jane Doe"
                                className="mt-2 w-full rounded-lg border border-[rgba(212,175,55,0.3)] bg-[#0d1117] px-3 py-2 text-sm text-brand-ivory focus:border-brand-gold focus:outline-none"
                            />
                        </label>
                        <label className="block">
                            <span className="text-[10px] uppercase tracking-[0.3em] text-brand-goldSoft">
                                Customer ID → Dropbox folder
                            </span>
                            <input
                                type="text"
                                required
                                value={form.customer_id}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        customer_id: e.target.value,
                                    })
                                }
                                placeholder="CUST-12345"
                                className="mt-2 w-full rounded-lg border border-[rgba(212,175,55,0.3)] bg-[#0d1117] px-3 py-2 font-mono text-sm text-brand-ivory focus:border-brand-gold focus:outline-none"
                            />
                            <p className="mt-1 text-[10px] text-brand-muted">
                                Files are read from{" "}
                                <code>/Customers/{form.customer_id || "<id>"}/</code>
                            </p>
                        </label>
                        <label className="block md:col-span-2">
                            <span className="text-[10px] uppercase tracking-[0.3em] text-brand-goldSoft">
                                Notes (internal — never shown to customer)
                            </span>
                            <textarea
                                value={form.notes}
                                onChange={(e) =>
                                    setForm({ ...form, notes: e.target.value })
                                }
                                rows={2}
                                className="mt-2 w-full rounded-lg border border-[rgba(212,175,55,0.25)] bg-[#0d1117] px-3 py-2 text-sm text-brand-ivory focus:border-brand-gold focus:outline-none"
                            />
                        </label>
                        {error && (
                            <p
                                role="alert"
                                className="md:col-span-2 rounded-md border border-red-500/40 bg-red-500/5 px-3 py-2 text-xs text-red-200"
                            >
                                <AlertTriangle
                                    aria-hidden="true"
                                    className="mr-1 inline h-3 w-3"
                                />
                                {error}
                            </p>
                        )}
                        <div className="md:col-span-2 flex items-center gap-3">
                            <button
                                type="submit"
                                disabled={saving}
                                className="inline-flex items-center gap-2 rounded-full border border-brand-gold bg-brand-gold px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-brand-bg hover:bg-brand-gold2 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                {saving ? (
                                    <Loader2
                                        aria-hidden="true"
                                        className="h-4 w-4 animate-spin"
                                    />
                                ) : editingId ? (
                                    "Save changes"
                                ) : (
                                    "Create user"
                                )}
                            </button>
                            {editingId && (
                                <button
                                    type="button"
                                    onClick={resetForm}
                                    className="text-xs uppercase tracking-[0.2em] text-brand-goldSoft hover:underline"
                                >
                                    Cancel
                                </button>
                            )}
                            {notice && (
                                <p className="text-xs text-emerald-300">
                                    {notice}
                                </p>
                            )}
                        </div>
                    </form>
                </div>

                {/* User list */}
                <div className="mt-10 flex items-center gap-2">
                    <Users
                        aria-hidden="true"
                        className="h-4 w-4 text-brand-gold2"
                    />
                    <p className="text-[11px] uppercase tracking-[0.4em] text-brand-goldSoft">
                        Portal users ({users?.length ?? 0})
                    </p>
                </div>
                <div className="mt-4 overflow-hidden rounded-2xl border border-[rgba(212,175,55,0.18)] bg-[#0b0e13]">
                    {users === null ? (
                        <div className="flex items-center gap-3 px-5 py-8 text-sm text-brand-muted">
                            <Loader2
                                aria-hidden="true"
                                className="h-4 w-4 animate-spin text-brand-gold2"
                            />
                            Loading…
                        </div>
                    ) : users.length === 0 ? (
                        <p className="px-5 py-8 text-sm text-brand-muted">
                            No portal users yet. Create one above.
                        </p>
                    ) : (
                        <table className="w-full border-collapse text-sm">
                            <thead>
                                <tr className="border-b border-[rgba(212,175,55,0.15)] text-left text-[10px] uppercase tracking-[0.3em] text-brand-goldSoft">
                                    <th className="px-4 py-3 font-medium">Email</th>
                                    <th className="hidden px-4 py-3 font-medium md:table-cell">
                                        Name
                                    </th>
                                    <th className="px-4 py-3 font-medium">
                                        Customer ID
                                    </th>
                                    <th className="hidden px-4 py-3 font-medium lg:table-cell">
                                        Last login
                                    </th>
                                    <th className="px-4 py-3 font-medium">Status</th>
                                    <th className="px-4 py-3 text-right font-medium">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map((u) => (
                                    <tr
                                        key={u.id}
                                        className="border-b border-[rgba(212,175,55,0.08)] last:border-b-0"
                                    >
                                        <td className="px-4 py-3 align-top">
                                            <p className="font-medium text-brand-ivory">
                                                {u.email}
                                            </p>
                                            <p className="text-[11px] text-brand-muted md:hidden">
                                                {u.display_name}
                                            </p>
                                        </td>
                                        <td className="hidden px-4 py-3 align-top text-brand-ivory/85 md:table-cell">
                                            {u.display_name}
                                        </td>
                                        <td className="px-4 py-3 align-top font-mono text-brand-ivory/85">
                                            {u.customer_id}
                                        </td>
                                        <td className="hidden px-4 py-3 align-top text-brand-muted lg:table-cell">
                                            {formatDate(u.last_login_at)}
                                        </td>
                                        <td className="px-4 py-3 align-top">
                                            <span
                                                className={`inline-block rounded-full px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] ${
                                                    u.enabled
                                                        ? "border border-emerald-500/40 bg-emerald-500/10 text-emerald-200"
                                                        : "border border-rose-500/40 bg-rose-500/10 text-rose-200"
                                                }`}
                                            >
                                                {u.enabled ? "Enabled" : "Disabled"}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 align-top">
                                            <div className="flex justify-end gap-1.5">
                                                <button
                                                    type="button"
                                                    onClick={() => startEdit(u)}
                                                    title="Edit"
                                                    className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-[rgba(212,175,55,0.4)] text-brand-gold2 hover:bg-brand-gold hover:text-brand-bg"
                                                >
                                                    <PencilLine
                                                        aria-hidden="true"
                                                        className="h-3.5 w-3.5"
                                                    />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => toggleEnabled(u)}
                                                    title={
                                                        u.enabled
                                                            ? "Disable access"
                                                            : "Enable access"
                                                    }
                                                    className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-[rgba(212,175,55,0.4)] text-brand-gold2 hover:bg-brand-gold hover:text-brand-bg"
                                                >
                                                    <Power
                                                        aria-hidden="true"
                                                        className="h-3.5 w-3.5"
                                                    />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => deleteUser(u)}
                                                    title="Delete"
                                                    className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-rose-500/40 text-rose-200 hover:bg-rose-500/20"
                                                >
                                                    <Trash2
                                                        aria-hidden="true"
                                                        className="h-3.5 w-3.5"
                                                    />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </section>
        </main>
    );
}
